import { ButtonType } from 'ds/types';
import { IconName } from 'shared';

import {
  CHECKLIST_KEY_ADD_BUILDING_CONTACTS,
  CHECKLIST_KEY_ADD_VENDOR_ACCESS_INSTRUCTIONS,
  CHECKLIST_KEY_CONFIRM_BILLING_CONTACTS,
  CHECKLIST_KEY_CONFIRM_POC,
  CHECKLIST_KEY_PICK_INTERNET_PLAN,
  CHECKLIST_KEY_SELECT_IT_SERVICES,
  CHECKLIST_KEY_SETUP_CLEANING,
  CHECKLIST_KEY_SETUP_PANTRY,
  CHECKLIST_KEY_SHARE_ISP_NETWORK_DETAILS,
  CHECKLIST_KEY_START_OFFICE_MANUAL,
  CHECKLIST_KEY_UPLOAD_COI,
  CHECKLIST_KEY_UPLOAD_INSURANCE_REQUIREMENTS,
  CHECKLIST_KEY_UPLOAD_FLOORPLAN,
  CHECKLIST_KEY_UPLOAD_LEASE
} from './checklistKeys';

export type ChecklistItemState = 'default' | 'blocked' | 'completed' | 'skipped';
export type ChecklistItemStatus = 'waiting-on-codi' | null;

export interface ChecklistItem {
  key: string;
  title: string;
  description: string;
  state: ChecklistItemState;
  status?: ChecklistItemStatus;
  skippable?: boolean;
  startButtonText?: string;
  buttonType?: ButtonType;
  viewRoute?: string;
  buttonDisabled?: boolean;
  onStart?: () => void;
  onSkip?: () => void;
  skippedAt?: string;
}

export interface ChecklistSection {
  id: string;
  title: string;
  icon: IconName;
  items: ChecklistItem[];
}

export type ChecklistData = ChecklistSection[];

export const CHECKLIST_DATA: ChecklistData = [
  {
    id: 'basic-info',
    title: 'Basic information',
    icon: 'starOutline',
    items: [
      {
        key: CHECKLIST_KEY_CONFIRM_POC,
        title: "Confirm your team's on-site point of contact",
        description:
          "Tell us who should manage your office in Codi. They'll have full access to payments and team settings. You can update or add teammates anytime from the Team page.",
        state: 'default',
        skippable: false,
        startButtonText: 'Start'
      },
      {
        key: CHECKLIST_KEY_UPLOAD_LEASE,
        title: 'Upload your signed lease',
        description: "Instantly teach your AI office assistant what's yours to handle vs. what's on the building.",
        state: 'default',
        skippable: true,
        startButtonText: 'Start'
      },
      {
        key: CHECKLIST_KEY_ADD_BUILDING_CONTACTS,
        title: 'Add building contacts',
        description:
          'Share contact information for your primary building contacts, like your property manager or superintendent.',
        state: 'default',
        skippable: true,
        startButtonText: 'Start'
      },
      {
        key: CHECKLIST_KEY_CONFIRM_BILLING_CONTACTS,
        title: 'Confirm billing contacts',
        description:
          'Billing emails will be sent to these contacts. Anyone you invite will have full access to your Codi office portal, including payment and team settings. You can always reassign these contacts later.',
        state: 'default',
        skippable: true,
        startButtonText: 'Start'
      }
    ]
  },
  {
    id: 'office-details',
    title: 'Office details',
    icon: 'meetingRoom',
    items: [
      {
        key: CHECKLIST_KEY_ADD_VENDOR_ACCESS_INSTRUCTIONS,
        title: 'Add vendor access instructions',
        description: 'Help vendors access your office — like cleaners, delivery drivers, or installers.',
        state: 'default',
        skippable: false,
        startButtonText: 'Start'
      },
      {
        key: CHECKLIST_KEY_UPLOAD_COI,
        title: 'Upload your Certificate of Insurance',
        description: "Help our AI office assistant understand what's covered.",
        state: 'default',
        skippable: false,
        startButtonText: 'Start'
      },
      {
        key: CHECKLIST_KEY_UPLOAD_INSURANCE_REQUIREMENTS,
        title: 'Upload insurance requirements for vendors',
        description: "Your building contact can provide this. We'll need a COI for every vendor on site.",
        state: 'default',
        skippable: true,
        startButtonText: 'Start'
      },
      {
        key: CHECKLIST_KEY_UPLOAD_FLOORPLAN,
        title: 'Upload your floorplan',
        description: 'Share a PDF, PNG, or JPG file that includes the dimensions of your rooms.',
        state: 'default',
        skippable: true,
        startButtonText: 'Start'
      },
      {
        key: CHECKLIST_KEY_START_OFFICE_MANUAL,
        title: 'Start your office manual',
        description: 'Add instructions around receiving mail, temperature control, trash, and restroom access.',
        state: 'default',
        skippable: false,
        startButtonText: 'Start'
      }
    ]
  },
  {
    id: 'internet-it',
    title: 'Internet & IT',
    icon: 'wifi',
    items: [
      {
        key: CHECKLIST_KEY_PICK_INTERNET_PLAN,
        title: 'Pick your internet plan',
        description:
          'Your quotes are ready — compare providers, select your plan, and get connected.\nNo longer need this? Remove service',
        state: 'blocked',
        skippable: false,
        startButtonText: 'Start'
      },
      {
        key: CHECKLIST_KEY_SHARE_ISP_NETWORK_DETAILS,
        title: 'Share your ISP & network details',
        description: 'Centralize key internet information so your team and AI office assistant can refer to it.',
        state: 'default',
        skippable: true,
        startButtonText: 'Start'
      },
      {
        key: CHECKLIST_KEY_SELECT_IT_SERVICES,
        title: 'Select additional IT services',
        description: 'Order router setup, A/V, digital security, and access control services with recommended vendors.',
        state: 'default',
        skippable: true,
        startButtonText: 'Start'
      }
    ]
  },
  {
    id: 'recurring-services',
    title: 'Recurring services',
    icon: 'refresh',
    items: [
      {
        key: CHECKLIST_KEY_SETUP_CLEANING,
        title: 'Set up cleaning service',
        description: 'Schedule recurring cleanings for your office on your preferred days of week.',
        state: 'default',
        skippable: false,
        startButtonText: 'Start'
      },
      {
        key: CHECKLIST_KEY_SETUP_PANTRY,
        title: 'Set up pantry restocking',
        description: "Explore the shop and select items you'd like us to deliver and restock on a regular basis.",
        state: 'default',
        skippable: false,
        startButtonText: 'Start'
      }
    ]
  }
];
